import { useQuery } from "@tanstack/react-query";
import {
  getSuperAdminDiagnostics,
  getSuperAdminFiles,
  getSuperAdminJobs,
  getSuperAdminPerformance,
  getSuperAdminStreams,
} from "@/lib/api";
import { unscopedKey } from "@/lib/queryClient";
import type {
  AdminDiagnostics,
  AdminFileInventoryResponse,
  AdminJobsPayload,
  AdminPerformance,
  AdminStreamsPayload,
} from "@/lib/types";

/**
 * Operator-console TanStack Query hooks (admin-console-split PR 4).
 *
 * The `/api/super-admin/*` siblings of `features/admin/hooks/useAdminData.ts`
 * — same polling discipline (pause when the document is hidden, one shared
 * cache key per endpoint, no staleTime), different routes and cache
 * domains. The `super-*` domains are PRIVATE unscoped keys: they must NOT
 * be whitelisted in `PUBLIC_UNSCOPED_CACHE_DOMAINS`, so logout eviction
 * clears them (tanstack-query-scope-keys rules).
 *
 * The shell mounts one `useOperatorDiagnostics` (10 s) for the platform
 * health strip; route components layer their own cadence on top.
 */

/** Health strip + Operations page (10 s on the shell). */
export function useOperatorDiagnostics(options?: {
  refetchIntervalMs?: number | false;
  enabled?: boolean;
}) {
  const interval = options?.refetchIntervalMs ?? 10_000;
  return useQuery<AdminDiagnostics>({
    queryKey: unscopedKey("super-diagnostics"),
    queryFn: getSuperAdminDiagnostics,
    refetchInterval: interval === false ? false : interval,
    refetchIntervalInBackground: false,
    enabled: options?.enabled ?? true,
  });
}

/** Encoder/host telemetry (5 s on Pipeline + Live infrastructure). */
export function useOperatorPerformance(options?: {
  refetchIntervalMs?: number | false;
  enabled?: boolean;
}) {
  const interval = options?.refetchIntervalMs ?? 5_000;
  return useQuery<AdminPerformance>({
    queryKey: unscopedKey("super-performance"),
    queryFn: getSuperAdminPerformance,
    refetchInterval: interval === false ? false : interval,
    refetchIntervalInBackground: false,
    enabled: options?.enabled ?? true,
  });
}

/** Cross-club background-job strip (10 s on Operations). */
export function useOperatorJobs(options?: {
  refetchIntervalMs?: number | false;
  enabled?: boolean;
}) {
  const interval = options?.refetchIntervalMs ?? 10_000;
  return useQuery<AdminJobsPayload>({
    queryKey: unscopedKey("super-jobs"),
    queryFn: getSuperAdminJobs,
    refetchInterval: interval === false ? false : interval,
    refetchIntervalInBackground: false,
    enabled: options?.enabled ?? true,
  });
}

/** Storage inventory. One-shot by default (manual refresh on the page). */
export function useOperatorFiles(options?: {
  refetchIntervalMs?: number | false;
  enabled?: boolean;
}) {
  const interval = options?.refetchIntervalMs ?? false;
  return useQuery<AdminFileInventoryResponse>({
    queryKey: unscopedKey("super-files"),
    queryFn: getSuperAdminFiles,
    refetchInterval: interval === false ? false : interval,
    refetchIntervalInBackground: false,
    enabled: options?.enabled ?? true,
  });
}

/** Active viewer sessions + kill blocks (5 s on Live infrastructure). */
export function useOperatorStreams(options?: {
  refetchIntervalMs?: number | false;
  enabled?: boolean;
}) {
  const interval = options?.refetchIntervalMs ?? 10_000;
  return useQuery<AdminStreamsPayload>({
    queryKey: unscopedKey("super-streams"),
    queryFn: getSuperAdminStreams,
    refetchInterval: interval === false ? false : interval,
    refetchIntervalInBackground: false,
    enabled: options?.enabled ?? true,
  });
}
